import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { usePartner } from "../context/PartnerContext";

const Icons = {
  trader:      (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="2,14 7,9 11,12 18,5" /><polyline points="14,5 18,5 18,9" /></svg>),
  signalsync:  (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="8" strokeDasharray="3 2"/><circle cx="10" cy="10" r="3" fill="currentColor" stroke="none"/><line x1="10" y1="2" x2="10" y2="5" /><line x1="10" y1="15" x2="10" y2="18" /><line x1="2" y1="10" x2="5" y2="10" /><line x1="15" y1="10" x2="18" y2="10" /></svg>),
  investor:    (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7.5" /><path d="M10 6.5v3.75l2.25 2.25" /></svg>),
  earnings:    (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="7.5" /><path d="M10 6v1.5m0 5V14m-2.5-5.5c0-1.1.9-2 2.5-2s2.5.9 2.5 2-1 1.8-2.5 2-2.5.9-2.5 2 .9 2 2.5 2 2.5-.9 2.5-2" /></svg>),
  marketplace: (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h14l-1.5 7H4.5L3 5z" /><circle cx="7" cy="16" r="1" /><circle cx="14" cy="16" r="1" /></svg>),
  affiliate:   (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="10" r="2" /><circle cx="15" cy="5" r="2" /><circle cx="15" cy="15" r="2" /><line x1="7" y1="9" x2="13" y2="6" /><line x1="7" y1="11" x2="13" y2="14" /></svg>),
  profile:     (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="7" r="3" /><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" /></svg>),
  admin:       (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2l2 5h5l-4 3 1.5 5L10 12l-4.5 3L7 10 3 7h5z" /></svg>),
  academy:     (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2L2 7l8 5 8-5-8-5z"/><path d="M2 13l8 5 8-5"/><path d="M2 10l8 5 8-5"/></svg>),
  logout:      (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M13 3h4v14h-4" /><polyline points="8,13 13,10 8,7" /><line x1="3" y1="10" x2="13" y2="10" /></svg>),
  partnerAdmin: (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="14" height="12" rx="1.5" /><line x1="3" y1="8" x2="17" y2="8" /><circle cx="7" cy="12" r="1" /></svg>),
};

const NAV_BY_ROLE = {
  trader: [
    { to: "/trader",          label: "Dashboard",    icon: "trader",      end: true },
    { to: "/trader/earnings", label: "Earnings",     icon: "earnings" },
    { to: "/marketplace",     label: "Marketplace",  icon: "marketplace" },
    { to: "/affiliate",       label: "Affiliate",    icon: "affiliate" },
    { to: "/profile",         label: "Profile",      icon: "profile" },
  ],
  investor: [
    { to: "/investor",                     label: "Portfolio",        icon: "investor",    end: true },
    { to: "/marketplace",                  label: "Marketplace",      icon: "marketplace" },
    { to: "/investor/billing",             label: "Billing",          icon: "earnings" },
    { to: "/affiliate",                    label: "Affiliate",        icon: "affiliate" },
    { to: "/profile",                      label: "Profile",          icon: "profile" },
  ],
  analyst: [
    { to: "/analyst",          label: "SignalSync™",  icon: "signalsync",  end: true },
    { to: "/analyst/earnings", label: "Earnings",     icon: "earnings" },
    { to: "/marketplace",      label: "Marketplace",  icon: "marketplace" },
    { to: "/affiliate",        label: "Affiliate",    icon: "affiliate" },
    { to: "/profile",          label: "Profile",      icon: "profile" },
  ],
  admin: [
    { to: "/admin",       label: "Admin Panel", icon: "admin" },
    { to: "/academy",     label: "Academy ",  icon: "academy" },
    { to: "/marketplace", label: "Marketplace", icon: "marketplace" },
    { to: "/profile",     label: "Profile",     icon: "profile" },
  ],
};

// Role label — analyst = SignalSync™ provider
const ROLE_LABEL = {
  trader:   "Trader",
  investor: "Investor",
  analyst:  "SignalSync™",
  admin:    "Admin",
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return "CA";
};

// ── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ onClose, mobileOpen }) => {
  const { appName, logoUrl, partner, loading: brandLoading } = usePartner();
  const navigate  = useNavigate();
  const [userName, setUserName]         = useState("");
  const [isPartnerAdmin, setIsPartnerAdmin] = useState(false);
  const userRole = localStorage.getItem("user_role") || "trader";

  // Fetch display name
  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (!token) return;
    fetch("/api/profile", { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => {
        if (data.name) setUserName(data.name);
        else setUserName((data.email || "").split("@")[0] || "User");
      })
      .catch(() => {
        const email = localStorage.getItem("user_email") || "";
        setUserName(email.split("@")[0] || "User");
      });
  }, []);

  // Cek apakah user ini partner admin untuk domain partner saat ini - link
  // Partner Admin cuma muncul kalau backend confirm ok:true, TIDAK berdasarkan
  // user_role (is_partner_admin independen dari role trader/investor/analyst)
  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (!token) return;
    // CrunchAlpha admin selalu punya akses Admin Page lewat jalur is_admin
    // biasa - skip fetch ini sepenuhnya utk mereka (mencegah 404 noise di
    // console, is_partner_admin memang selalu false utk akun CrunchAlpha).
    const role = (localStorage.getItem("user_role") || "").toLowerCase();
    if (role === "admin") return;
    fetch("/api/partner-admin/config", { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => setIsPartnerAdmin(!!data.ok))
      .catch(() => setIsPartnerAdmin(false));
  }, []);


  const handleLogout = () => {
    ["access_token", "token", "user_role", "user_email"].forEach(k => localStorage.removeItem(k));
    navigate("/login");
  };

  const handleNav = () => { if (onClose) onClose(); };

  let navItems = NAV_BY_ROLE[userRole] || NAV_BY_ROLE.trader;

  // Partner Admin link muncul untuk SIAPAPUN role-nya (trader/investor/analyst)
  // kalau backend confirm is_partner_admin=true untuk domain partner ini -
  // disisipkan sebelum "Profile" (item terakhir di semua NAV_BY_ROLE array).
  if (isPartnerAdmin) {
    const profileIdx = navItems.findIndex(item => item.to === "/profile");
    const partnerAdminItem = { to: "/partner-admin", label: "Partner Admin", icon: "partnerAdmin" };
    navItems = profileIdx >= 0
      ? [...navItems.slice(0, profileIdx), partnerAdminItem, ...navItems.slice(profileIdx)]
      : [...navItems, partnerAdminItem];
  }

  const isAnalyst = userRole === "analyst";

  return (
    <aside
      className={"app-sidebar" + (mobileOpen ? " app-sidebar-open" : "")}
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "100vh", overflow: "hidden" }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ padding: "20px 18px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 10,
        }}>
          <div
            style={{ fontWeight: 800, fontSize: 16, letterSpacing: "-0.02em", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            onClick={() => { navigate("/"); handleNav(); }}
          >
            {(logoUrl || (!partner && !brandLoading)) && <img src={logoUrl || "/logo.jpeg"} alt={appName} style={{ height: 28, width: 28, borderRadius: 5, objectFit: "cover" }} onError={e => e.target.style.display = "none"} />}
            {appName === "CrunchAlpha" ? (<>Crunch<span style={{ color: "var(--accent)" }}>Alpha</span></>) : appName}
          </div>
          <button
            onClick={handleNav}
            className="sidebar-close-mobile"
            style={{ background: "none", border: "none", color: "var(--text-faint)", cursor: "pointer", padding: 4, lineHeight: 1, fontSize: 18 }}
          ></button>
        </div>

        {userName && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: isAnalyst ? "rgba(78,154,241,0.12)" : "var(--accent-soft)",
              border: `1px solid ${isAnalyst ? "rgba(78,154,241,0.3)" : "var(--accent-border)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 600, color: "var(--accent)", flexShrink: 0,
            }}>
              {isAnalyst ? Icons.signalsync : getInitials(userName)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 500, color: "var(--text-main)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{userName}</div>
              <div style={{ fontSize: 11, color: isAnalyst ? "var(--accent)" : "var(--text-faint)", marginTop: 1 }}>
                {ROLE_LABEL[userRole] || userRole}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Nav ────────────────────────────────────────────────────────────── */}
      <nav style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column", gap: 2 }}>
        {navItems.map(item => (
          <NavLink
            key={item.to} to={item.to} end={item.end}
            onClick={handleNav}
            className={({ isActive }) => "sidebar-link" + (isActive ? " sidebar-link-active" : "")}
          >
            {Icons[item.icon] || Icons.trader}
            {item.label}
          </NavLink>
        ))}

        <div style={{ marginTop: 16, borderTop: "1px solid var(--border-subtle)", paddingTop: 12 }}>
          <button
            onClick={handleLogout}
            style={{
              width: "100%", padding: "8px 12px",
              background: "transparent", border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)", color: "var(--text-muted)",
              fontSize: 13, fontWeight: 500, cursor: "pointer",
              textAlign: "left", display: "flex", alignItems: "center", gap: 8,
            }}
          >
            {Icons.logout} Sign out
          </button>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
